import type { User } from '../../lib/api';
import { colorForString, initialsFromName } from '../../lib/dashboardHelpers';
import type { PastelColor } from '../c2/Pill';

export interface OnboardingStep {
  id: string;
  label: string;
}

const pastelBg: Record<PastelColor, string> = {
  peach: 'var(--color-peach)',
  mint: 'var(--color-mint)',
  sky: 'var(--color-sky)',
  lemon: 'var(--color-lemon)',
  lilac: 'var(--color-lilac)',
};
const pastelDeep: Record<PastelColor, string> = {
  peach: 'var(--color-peach-deep)',
  mint: 'var(--color-mint-deep)',
  sky: 'var(--color-sky-deep)',
  lemon: 'var(--color-lemon-deep)',
  lilac: 'var(--color-lilac-deep)',
};

interface ProgressRailProps {
  steps: OnboardingStep[];
  currentIdx: number;
  completed: Set<string>;
  onJump: (i: number) => void;
  user: User | null;
}

export default function ProgressRail({ steps, currentIdx, completed, onJump, user }: ProgressRailProps) {
  const userName = user?.name ?? user?.email?.split('@')[0] ?? 'You';
  const userInitials = initialsFromName(userName);
  const userColor = user?.email ? colorForString(user.email) : 'peach';

  const pct = Math.round((completed.size / steps.length) * 100);

  return (
    <aside
      className="flex flex-col sticky top-0"
      style={{
        width: 280,
        flexShrink: 0,
        background: 'var(--color-bg)',
        borderRight: '1px solid var(--color-hairline)',
        height: '100vh',
        padding: '28px 24px',
      }}
    >
      <div className="flex items-center gap-2.5 mb-9">
        <div
          className="w-[34px] h-[34px] rounded-[9px] flex items-center justify-center font-mono font-bold text-[13px]"
          style={{ background: 'var(--accent-soft)', color: 'var(--accent-deep)' }}
        >
          go/
        </div>
        <span className="text-[15px] font-bold text-ink" style={{ letterSpacing: '-0.005em' }}>Tidy Go Links</span>
      </div>

      <div
        className="font-bold uppercase mb-[18px]"
        style={{ fontSize: 11, color: 'var(--color-ink-muted)', letterSpacing: '0.08em' }}
      >
        Get set up · {pct}%
      </div>

      <div className="relative">
        <div
          className="absolute"
          style={{
            left: 13,
            top: 12,
            bottom: 12,
            width: 2,
            background: 'var(--color-hairline-strong)',
            borderRadius: 1,
          }}
        />
        <div
          className="absolute transition-[height]"
          style={{
            left: 13,
            top: 12,
            width: 2,
            height: `calc(${Math.max(0, currentIdx)} * 44px)`,
            background: 'var(--accent-solid)',
            borderRadius: 1,
            transitionDuration: '300ms',
          }}
        />

        {steps.map((s, i) => {
          const done = completed.has(s.id);
          const active = i === currentIdx;
          const upcoming = i > currentIdx && !done;
          return (
            <button
              key={s.id}
              onClick={() => onJump(i)}
              className="flex items-center w-full bg-transparent border-0 cursor-pointer text-left transition-colors duration-150 relative"
              style={{
                gap: 12,
                padding: '8px 0',
                color: active ? 'var(--color-ink)' : upcoming ? 'var(--color-ink-muted)' : 'var(--color-ink-soft)',
                fontSize: 13.5,
                fontWeight: active ? 700 : done ? 600 : 500,
              }}
            >
              <span
                className="rounded-full flex items-center justify-center shrink-0 transition-all"
                style={{
                  width: 28,
                  height: 28,
                  background: done ? 'var(--accent-solid)' : active ? 'var(--color-surface)' : 'var(--color-bg-alt)',
                  border: active
                    ? '2px solid var(--accent-solid)'
                    : `1.5px solid ${done ? 'var(--accent-solid)' : 'var(--color-hairline-strong)'}`,
                  color: done ? '#fff' : active ? 'var(--accent-deep)' : 'var(--color-ink-muted)',
                  fontSize: 11,
                  fontWeight: 700,
                  zIndex: 1,
                  transitionDuration: '200ms',
                }}
              >
                {done ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  i + 1
                )}
              </span>
              <span>{s.label}</span>
            </button>
          );
        })}
      </div>

      <div className="flex-1" />

      <div
        className="flex items-center gap-2.5 pt-4"
        style={{ borderTop: '1px solid var(--color-hairline)' }}
      >
        <div
          className="w-[30px] h-[30px] rounded-full flex items-center justify-center font-bold text-xs"
          style={{ background: pastelBg[userColor], color: pastelDeep[userColor] }}
        >
          {userInitials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-semibold text-ink leading-tight">{userName}</div>
          <div className="text-[11px] text-ink-muted leading-tight mt-px overflow-hidden text-ellipsis whitespace-nowrap">
            {user?.email ?? ''}
          </div>
        </div>
      </div>
    </aside>
  );
}
